import React from 'react';
import { InstrumentForgeProfile } from '../types/instrumentForge.types';
import { Music, Check, Zap, Key, Gauge } from 'lucide-react';
import { Badge } from '../../../components/ui';

type ProfileCardVariant = 'default' | 'compact' | 'browser';

interface ProfileCardProps {
  profile: InstrumentForgeProfile;
  compact?: boolean;
  variant?: ProfileCardVariant;
  isSelected?: boolean;
  isExcluded?: boolean;
  onClick?: () => void;
}

const ProfileCard: React.FC<ProfileCardProps> = ({
  profile,
  compact = false,
  variant: variantProp,
  isSelected = false,
  isExcluded = false,
  onClick,
}) => {
  const variant: ProfileCardVariant = variantProp ?? (compact ? 'compact' : 'default');

  // Browser variant - rich metadata card for lightbox grid
  if (variant === 'browser') {
    const [tempoMin, tempoMax] = profile.tempoRange;
    const [energyMin, energyMax] = profile.energyRange;

    return (
      <button
        type="button"
        onClick={onClick}
        className={`
          relative text-left w-full p-4 rounded-2xl border-2 transition-all
          hover:border-accent/50 hover:bg-slate-800/40 active:scale-[0.98]
          ${isSelected
            ? 'border-accent bg-accent/5 shadow-lg shadow-amber-500/10'
            : isExcluded
              ? 'border-slate-800 bg-slate-950/40 opacity-50'
              : 'border-slate-800 bg-slate-950/40'
          }
        `}
      >
        {/* Selected check badge */}
        {isSelected && (
          <div className="absolute top-3 right-3 w-6 h-6 rounded-full bg-accent flex items-center justify-center text-slate-950">
            <Check size={14} strokeWidth={3} />
          </div>
        )}

        {/* Excluded label */}
        {isExcluded && (
          <div className="absolute top-3 right-3">
            <Badge variant="default-muted" size="xs">In other slot</Badge>
          </div>
        )}

        {/* Header */}
        <div className="flex items-center gap-3 mb-3">
          <div className="p-2 bg-accent/10 rounded-lg text-accent flex-shrink-0">
            <Music size={18} />
          </div>
          <div className="min-w-0">
            <h4 className="text-sm font-black text-slate-100 uppercase tracking-tight truncate">{profile.name}</h4>
            <p className="text-[11px] text-slate-400 font-bold uppercase tracking-tighter">{profile.identity.era} &bull; {profile.identity.movement}</p>
          </div>
        </div>

        {/* Description */}
        <p className="text-[11px] text-slate-400 leading-relaxed italic line-clamp-2 mb-3">"{profile.description}"</p>

        {/* Genre badges */}
        <div className="flex flex-wrap gap-1 mb-3">
          {profile.genres.slice(0, 4).map(genre => (
            <Badge key={genre} variant="primary-muted" size="xs" pill className="font-bold uppercase">
              {genre}
            </Badge>
          ))}
          {profile.genres.length > 4 && (
            <Badge variant="default-muted" size="xs" pill>+{profile.genres.length - 4}</Badge>
          )}
        </div>

        {/* Metadata row */}
        <div className="grid grid-cols-3 gap-2 pt-3 border-t border-slate-800/60">
          {/* Tempo */}
          <div className="space-y-1">
            <div className="flex items-center gap-1 text-slate-400">
              <Gauge size={10} />
              <span className="text-[11px] font-black uppercase tracking-widest">Tempo</span>
            </div>
            <span className="text-[11px] text-slate-300 font-bold block">{tempoMin}-{tempoMax} BPM</span>
          </div>

          {/* Energy */}
          <div className="space-y-1">
            <div className="flex items-center gap-1 text-slate-400">
              <Zap size={10} />
              <span className="text-[11px] font-black uppercase tracking-widest">Energy</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="flex-1 h-1.5 bg-slate-800 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-accent/60 to-accent rounded-full"
                  style={{
                    marginLeft: `${energyMin * 10}%`,
                    width: `${(energyMax - energyMin) * 10}%`,
                  }}
                />
              </div>
              <span className="text-[11px] text-slate-400 font-mono">{energyMin}-{energyMax}</span>
            </div>
          </div>

          {/* Keys */}
          <div className="space-y-1">
            <div className="flex items-center gap-1 text-slate-400">
              <Key size={10} />
              <span className="text-[11px] font-black uppercase tracking-widest">Keys</span>
            </div>
            <span className="text-[11px] text-slate-300 font-bold block truncate">{profile.preferredKeys.join(', ')}</span>
          </div>
        </div>

        {/* Specialty instruments */}
        {profile.specialty && profile.specialty.length > 0 && (
          <div className="mt-2 pt-2 border-t border-slate-800/40">
            <div className="flex flex-wrap gap-1">
              {profile.specialty.map(s => (
                <Badge key={s.name} variant="info-muted" size="xs" pill className="font-bold">
                  {s.name}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </button>
    );
  }

  // Default and compact variants (original behavior)
  return (
    <div className="bg-slate-950 border border-slate-800 rounded-xl p-4 space-y-3">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-accent/10 rounded-lg text-accent">
          <Music size={18} />
        </div>
        <div>
          <h4 className="text-sm font-black text-slate-100 uppercase tracking-tight">{profile.name}</h4>
          <p className="text-[11px] text-slate-400 font-bold uppercase tracking-tighter">{profile.identity.era} &bull; {profile.identity.movement}</p>
        </div>
      </div>

      {variant === 'default' && (
        <>
          <p className="text-xs text-slate-400 leading-relaxed italic">"{profile.description}"</p>
          <div className="grid grid-cols-2 gap-4 pt-2 border-t border-slate-900">
            <div className="space-y-1">
              <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest block">Drums</span>
              <span className="text-[11px] text-slate-400 block line-clamp-1">{profile.rhythmSection.drums.character}</span>
            </div>
            <div className="space-y-1">
              <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest block">Guitars</span>
              <span className="text-[11px] text-slate-400 block line-clamp-1">{profile.guitars.rhythmLeft.character}</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ProfileCard;
